import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCountUp } from '@/hooks/useCountUp';
import { getUpcomingEvents, type FrontendEvent } from '@/services/events.api';

const HERO_BG = '/images/hero-bg-main.jpg';

const STATS = [
  { target: 30, suffix: '+', labelKey: 'hero.stat1.label' },
  { target: 150, suffix: '+', labelKey: 'hero.stat2.label' },
  { target: 50, suffix: '+', labelKey: 'hero.stat3.label' },
];

// Fallback summit info (used when no upcoming event is found in backend)
const FALLBACK_SUMMIT_DATE = new Date('2026-04-15T09:00:00');
const FALLBACK_SUMMIT_NAME_ZH = '2026 全球科创峰会';
const FALLBACK_SUMMIT_NAME_EN = '2026 Global Tech Innovation Summit';
const FALLBACK_SUMMIT_LOCATION_ZH = '加州，美国';
const FALLBACK_SUMMIT_LOCATION_EN = 'California, USA';

interface SummitInfo {
  date: Date;
  nameZh: string;
  nameEn: string;
  locationZh: string;
  locationEn: string;
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

function calcTimeLeft(target: Date): TimeLeft {
  const now = new Date().getTime();
  const diff = Math.max(0, target.getTime() - now);
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((diff % (1000 * 60)) / 1000),
  };
}

function CountdownBlock({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex flex-col items-center">
      <div className="relative">
        <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center backdrop-blur-sm">
          <span className="text-xl md:text-2xl font-bold text-white tabular-nums leading-none">
            {String(value).padStart(2, '0')}
          </span>
        </div>
      </div>
      <span className="mt-1.5 text-[10px] tracking-widest uppercase text-white/40 font-medium">{label}</span>
    </div>
  );
}

function SummitCountdown({ isZh }: { isZh: boolean }) {
  // Dynamically fetch the nearest upcoming event from backend API
  const [summit, setSummit] = useState<SummitInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchNextSummit() {
      try {
        const events = await getUpcomingEvents();
        const now = new Date();
        
        // Filter and sort upcoming events (includes both 'upcoming' and 'published' from backend)
        const upcoming = events
          .filter((e: FrontendEvent) => (e.status === 'upcoming' || e.status === 'published') && e.date)
          .map((e: FrontendEvent) => ({ ...e, _d: new Date(e.date) }))
          .filter((e) => e._d > now)
          .sort((a, b) => a._d.getTime() - b._d.getTime());

        if (upcoming.length > 0) {
          const ev = upcoming[0];
          setSummit({
            date: ev._d,
            nameZh: ev.title || FALLBACK_SUMMIT_NAME_ZH,
            nameEn: ev.titleEn || ev.title || FALLBACK_SUMMIT_NAME_EN,
            locationZh: ev.location || FALLBACK_SUMMIT_LOCATION_ZH,
            locationEn: ev.locationEn || ev.location || FALLBACK_SUMMIT_LOCATION_EN,
          });
        } else {
          // No upcoming events, use fallback
          setSummit({
            date: FALLBACK_SUMMIT_DATE,
            nameZh: FALLBACK_SUMMIT_NAME_ZH,
            nameEn: FALLBACK_SUMMIT_NAME_EN,
            locationZh: FALLBACK_SUMMIT_LOCATION_ZH,
            locationEn: FALLBACK_SUMMIT_LOCATION_EN,
          });
        }
      } catch (error) {
        console.error('Failed to fetch upcoming events:', error);
        // API failed, use fallback
        setSummit({
          date: FALLBACK_SUMMIT_DATE,
          nameZh: FALLBACK_SUMMIT_NAME_ZH,
          nameEn: FALLBACK_SUMMIT_NAME_EN,
          locationZh: FALLBACK_SUMMIT_LOCATION_ZH,
          locationEn: FALLBACK_SUMMIT_LOCATION_EN,
        });
      } finally {
        setLoading(false);
      }
    }
    
    fetchNextSummit();
  }, []);

  const [timeLeft, setTimeLeft] = useState<TimeLeft>(() => calcTimeLeft(new Date()));
  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    if (!summit) return;
    
    // Initial calculation
    setTimeLeft(calcTimeLeft(summit.date));
    
    const timer = setInterval(() => {
      setTimeLeft(calcTimeLeft(summit.date));
      setPulse((p) => !p);
    }, 1000);
    return () => clearInterval(timer);
  }, [summit]);

  const labels = isZh
    ? { days: '天', hours: '时', minutes: '分', seconds: '秒' }
    : { days: 'DAYS', hours: 'HRS', minutes: 'MIN', seconds: 'SEC' };

  const isOver = timeLeft.days === 0 && timeLeft.hours === 0 && timeLeft.minutes === 0 && timeLeft.seconds === 0;

  // Loading state
  if (loading) {
    return (
      <div className="mt-10 flex flex-col items-center">
        <div className="flex items-center gap-2 mb-4">
          <span className="w-2 h-2 rounded-full bg-[#E8A838] animate-pulse" />
          <span className="text-white/60 text-xs tracking-widest uppercase font-medium">
            {isZh ? '距下一场峰会' : 'Next Summit Countdown'}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex flex-col items-center">
            <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center backdrop-blur-sm">
              <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            </div>
            <span className="mt-1.5 text-[10px] tracking-widest uppercase text-white/40 font-medium">
              {labels.days}
            </span>
          </div>
          <span className="text-white/40 text-2xl font-light mb-7">:</span>
          <div className="flex flex-col items-center">
            <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center backdrop-blur-sm">
              <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            </div>
            <span className="mt-1.5 text-[10px] tracking-widest uppercase text-white/40 font-medium">
              {labels.hours}
            </span>
          </div>
          <span className="text-white/40 text-2xl font-light mb-7">:</span>
          <div className="flex flex-col items-center">
            <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center backdrop-blur-sm">
              <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            </div>
            <span className="mt-1.5 text-[10px] tracking-widest uppercase text-white/40 font-medium">
              {labels.minutes}
            </span>
          </div>
          <span className="text-white/40 text-2xl font-light mb-7">:</span>
          <div className="flex flex-col items-center">
            <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center backdrop-blur-sm">
              <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            </div>
            <span className="mt-1.5 text-[10px] tracking-widest uppercase text-white/40 font-medium">
              {labels.seconds}
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (!summit) {
    return null;
  }

  return (
    <div className="mt-10 flex flex-col items-center">
      {/* Event info chip */}
      <div className="flex items-center gap-2 mb-4">
        <span className="w-2 h-2 rounded-full bg-[#E8A838] animate-pulse" />
        <span className="text-white/60 text-xs tracking-widest uppercase font-medium">
          {isZh ? '距下一场峰会' : 'Next Summit Countdown'}
        </span>
      </div>

      {isOver ? (
        <div className="text-white/50 text-sm tracking-widest uppercase">
          {isZh ? '活动进行中' : 'Event In Progress'}
        </div>
      ) : (
        /* Countdown blocks */
        <div className="flex items-end gap-3">
          <CountdownBlock value={timeLeft.days} label={labels.days} />
          <span className={`text-white/40 text-2xl font-light mb-7 transition-opacity duration-500 ${pulse ? 'opacity-100' : 'opacity-20'}`}>:</span>
          <CountdownBlock value={timeLeft.hours} label={labels.hours} />
          <span className={`text-white/40 text-2xl font-light mb-7 transition-opacity duration-500 ${pulse ? 'opacity-100' : 'opacity-20'}`}>:</span>
          <CountdownBlock value={timeLeft.minutes} label={labels.minutes} />
          <span className={`text-white/40 text-2xl font-light mb-7 transition-opacity duration-500 ${pulse ? 'opacity-100' : 'opacity-20'}`}>:</span>
          <CountdownBlock value={timeLeft.seconds} label={labels.seconds} />
        </div>
      )}

      {/* Event name & location */}
      <div className="mt-4 flex flex-col sm:flex-row items-center gap-2 text-center">
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-calendar-event-line text-[#E8A838] text-sm" />
          </div>
          <span className="text-white/80 text-sm font-semibold">
            {isZh ? summit.nameZh : summit.nameEn}
          </span>
        </div>
        <span className="hidden sm:block text-white/30">·</span>
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-map-pin-2-line text-white/40 text-sm" />
          </div>
          <span className="text-white/50 text-sm">
            {isZh ? summit.locationZh : summit.locationEn}
          </span>
        </div>
        <span className="hidden sm:block text-white/30">·</span>
        <Link
          to="/events"
          className="text-[#7EC8E3] text-xs hover:text-white transition-colors flex items-center gap-1 whitespace-nowrap cursor-pointer"
        >
          {isZh ? '查看详情' : 'View Details'}
          <div className="w-3 h-3 flex items-center justify-center">
            <i className="ri-arrow-right-line text-xs" />
          </div>
        </Link>
      </div>
    </div>
  );
}

function CountUpStat({ target, suffix, labelKey, isVisible }: { target: number; suffix: string; labelKey: string; isVisible: boolean }) {
  const { t } = useTranslation();
  const count = useCountUp(target, isVisible, 2200);
  return (
    <div className="text-center">
      <div className="text-3xl md:text-4xl font-bold text-white mb-1 tabular-nums">
        {count}{suffix}
      </div>
      <div className="text-white/50 text-sm tracking-widest uppercase">
        {t(labelKey)}
      </div>
    </div>
  );
}

export default function Hero() {
  const { t, i18n } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [statsVisible, setStatsVisible] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);
  const isZh = i18n.language === 'zh';

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsVisible(true); },
      { threshold: 0.5 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden">
      {/* Background */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${HERO_BG})` }}
      />
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A2540]/80 via-[#0A2540]/70 to-[#0A2540]/90" />
      {/* Grid Overlay */}
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `linear-gradient(rgba(44,125,160,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(44,125,160,0.3) 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
        }}
      />

      {/* Content */}
      <div
        className={`relative z-10 w-full text-center px-4 md:px-6 transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        <div className="max-w-4xl mx-auto">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#2C7DA0]/20 border border-[#2C7DA0]/40 text-[#7EC8E3] text-xs font-medium mb-6 tracking-widest uppercase">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-global-line" />
            </div>
            <span>{t('hero.badge')}</span>
          </div>

          {/* Title */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-4">
            {t('hero.title')}
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2C7DA0] to-[#7EC8E3]">
              {t('hero.titleHighlight')}
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-white/70 text-base md:text-lg max-w-2xl mx-auto leading-relaxed mb-10">
            {t('hero.subtitle')}
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/about"
              className="px-8 py-3.5 bg-[#2C7DA0] hover:bg-[#2C7DA0]/90 text-white rounded-lg font-medium text-sm transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              {t('hero.cta.learn')}
            </Link>
            <Link
              to="/events"
              className="px-8 py-3.5 bg-white/10 hover:bg-white/20 border border-white/30 hover:border-white/50 text-white rounded-lg font-medium text-sm transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              {t('hero.cta.events')}
              <i className="ri-arrow-right-line ml-2" />
            </Link>
            <Link
              to="/partners#apply"
              className="px-8 py-3.5 bg-gradient-to-r from-[#E8A838] to-[#F5C842] hover:from-[#E8A838]/90 hover:to-[#F5C842]/90 text-[#0A2540] rounded-lg font-semibold text-sm transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-links-line" />
              </div>
              {t('hero.cta.apply')}
            </Link>
          </div>

          {/* ====== SUMMIT COUNTDOWN ====== */}
          <SummitCountdown isZh={isZh} />
        </div>

        {/* Stats with count-up animation */}
        <div
          ref={statsRef}
          className="mt-14 md:mt-20 flex flex-col sm:flex-row items-center justify-center gap-8 sm:gap-16"
        >
          {STATS.map(({ target, suffix, labelKey }) => (
            <CountUpStat
              key={labelKey}
              target={target}
              suffix={suffix}
              labelKey={labelKey}
              isVisible={statsVisible}
            />
          ))}
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center pt-2">
          <div className="w-1 h-2 bg-white/50 rounded-full" />
        </div>
      </div>
    </section>
  );
}
